"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.FeatureFlag = exports.LOCAL_STORAGE_FLAGS_LIST_KEY = void 0;
const tslib_1 = require("tslib");
const Http_1 = tslib_1.__importDefault(require("./Http"));
exports.LOCAL_STORAGE_FLAGS_LIST_KEY = 'sk-feature-flags-list';
const DEFAULT_REFRESH_SCHEDULE_MINUTES = 5;
let autoRefreshInterval = undefined;
const DEFAULT_OPTIONS = {
    url: 'https://feature-flag-api.samknows.one'
};
exports.FeatureFlag = {
    apiUrl: DEFAULT_OPTIONS.url,
    /**
     * - Fetch and cache flags
     * - Set up auto refresh if autoRefresh options is passed
     * - Use given URL to point to the right environment
     *
     * Best to use auto refresh when flags are needed at route level
     *
     * @param  {InitOptions} options
     * @returns void
     */
    init(options = DEFAULT_OPTIONS) {
        var _a, _b;
        const autoRefresh = (_a = options.autoRefresh) !== null && _a !== void 0 ? _a : false;
        this.apiUrl = (_b = options.url) !== null && _b !== void 0 ? _b : DEFAULT_OPTIONS.url;
        // fetch and cache flags
        this.fetchFlagsList();
        if (autoRefresh) {
            this.setupRefreshFlagsList();
        }
    },
    /**
     * Check if a flag is enabled or not by checking the cached feature flags list
     * Best to use when need to use more than one flag or in more than one place
     * Remember to refresh cached flags before by calling fetchFlagsList
     *
     * @param  {string} flagName
     * @returns boolean
     */
    isEnabled(flagName) {
        try {
            const cachedFlags = localStorage.getItem(exports.LOCAL_STORAGE_FLAGS_LIST_KEY);
            const flagList = JSON.parse(cachedFlags) || {};
            return flagList[flagName] || false;
        }
        catch (_a) {
            return false;
        }
    },
    /**
     * Check if a flag is enabled or not in real time by using REST API
     * Best to use when need to use one flag in one place and in real time
     *
     * @param  {string} flagName
     * @returns Promise<boolean>
     */
    fetchFlagState(flagName) {
        return tslib_1.__awaiter(this, void 0, void 0, function* () {
            try {
                const { data } = yield Http_1.default.request(`${this.apiUrl}/feature-flags/${flagName}`, {
                    useAuth: false
                });
                return data.state;
            }
            catch (_a) {
                return false;
            }
        });
    },
    /**
     * Get full list of flags and cache them in local storage
     * Best to use when need to use more than one flag or in more than one place
     * Use isEnabled function to check a given flag state after getting the list
     *
     * @returns Promise<boolean>
     */
    fetchFlagsList() {
        return tslib_1.__awaiter(this, void 0, void 0, function* () {
            try {
                const { data } = yield Http_1.default.request(`${this.apiUrl}/feature-flags`, {
                    useAuth: false
                });
                const flagList = data.reduce((list, item) => {
                    list[item.name] = item.state;
                    return list;
                }, {});
                localStorage.setItem(exports.LOCAL_STORAGE_FLAGS_LIST_KEY, JSON.stringify(flagList));
                return flagList;
            }
            catch (_a) {
                return {};
            }
        });
    },
    /**
     * @private
     *
     * Trigger feature flag refresh after given time
     */
    setAutoRefreshInterval() {
        const AUTO_REFRESH_TIMEOUT_MS = DEFAULT_REFRESH_SCHEDULE_MINUTES * 60 * 1000;
        // Set interval
        autoRefreshInterval = setInterval(() => {
            this.fetchFlagsList();
        }, AUTO_REFRESH_TIMEOUT_MS);
    },
    /**
     * @private
     *
     * Only refresh feature flags when tab (e.g user) is active to prevent unnecessary calls
     */
    handleBrowserTabInactive() {
        const isBrowserTabActive = !document.hidden;
        if (isBrowserTabActive) {
            this.fetchFlagsList();
            this.setAutoRefreshInterval();
        }
        else {
            clearInterval(autoRefreshInterval);
        }
    },
    /**
     * @private
     *
     * Refresh the list of cached flags if they are outdated
     * Flags expire after 5mins
     *
     * @returns Promise<void>
     */
    setupRefreshFlagsList() {
        this.setAutoRefreshInterval();
        document.addEventListener('visibilitychange', this.handleBrowserTabInactive);
    },
    /**
     * Any clean up required when flags are no longer needed
     *
     * @returns void
     */
    cleanUp() {
        localStorage.removeItem(exports.LOCAL_STORAGE_FLAGS_LIST_KEY);
        document.removeEventListener('visibilitychange', this.handleBrowserTabInactive);
    }
};
//# sourceMappingURL=FeatureFlag.js.map